import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { requestPermission } from '../api/permission-bridge';
import { closedGateModalState, gateModalAtom } from '../atoms';
import { readPermissionStatuses } from '../lib/check-permissions';
import { deriveMissingPermissions } from '../lib/derive-missing-permissions';
import { requiredPermissions } from '../matrix';
import type {
  DesktopPermissionGatePresentation,
  DesktopPermissionGateVariant,
  PermissionMode,
  PermissionName,
} from '../types';
import { DesktopPermissionModal } from './desktop-permission-modal';
import { DesktopPermissionOverlayCard } from './desktop-permission-overlay-card';

const POLL_INTERVAL_MS = 1500;

const resolvePresentationVariant = (
  variant: DesktopPermissionGatePresentation['variant'],
  mode: PermissionMode
): DesktopPermissionGateVariant => {
  if (typeof variant === 'string') return variant;
  return mode === 'computer_use'
    ? (variant.computerUse ?? variant.default)
    : (variant.recording ?? variant.default);
};

/**
 * Renders the desktop permission gate UI (dialog or overlay-card) from the
 * shared `gateModalAtom`. Mount once per window as a sibling — it does not wrap
 * children, so it adds no provider to the app tree. The gate is driven by
 * `useDesktopPermissionGate().ensurePermissions(...)`, which opens the modal.
 */
export function DesktopPermissionGateHost({
  variant,
}: DesktopPermissionGatePresentation) {
  const [modal, setModal] = useAtom(gateModalAtom);

  const refreshStatuses = useCallback(
    async (mode: PermissionMode, missing: PermissionName[]) => {
      const required = requiredPermissions(mode);
      const statuses = await readPermissionStatuses(required);
      if (deriveMissingPermissions(required, statuses).length === 0) {
        // Last required permission just granted — dismiss instead of leaving an
        // empty "grant…then retry" modal.
        setModal(closedGateModalState());
        return statuses;
      }
      setModal((current) => ({
        ...current,
        statuses,
        missing: missing.filter((permission) => statuses[permission] !== 'granted'),
      }));
      return statuses;
    },
    [setModal]
  );

  const closeModal = useCallback(() => {
    setModal(closedGateModalState());
  }, [setModal]);

  const missingKey = modal.missing.join(',');

  // Recording can recover in-place after System Settings changes. Computer-use
  // spans the project page and overlay, so users restart Heidi after granting
  // permissions to avoid split state between those surfaces.
  useEffect(() => {
    if (!modal.open) return;
    if (modal.mode === 'computer_use') return;

    const required = requiredPermissions(modal.mode);
    let cancelled = false;
    let inFlight = false;
    let interval: number | undefined;

    const stop = () => {
      if (interval !== undefined) window.clearInterval(interval);
      interval = undefined;
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const statuses = await readPermissionStatuses(required);
        if (cancelled) return;
        if (deriveMissingPermissions(required, statuses).length === 0) {
          // All permissions granted out of band — dismiss the gate so the user
          // can retry the original action, rather than showing an empty modal.
          stop();
          setModal(closedGateModalState());
          return;
        }
        setModal((current) => {
          const unchanged = required.every(
            (permission) => current.statuses[permission] === statuses[permission]
          );
          if (unchanged) return current;
          return {
            ...current,
            statuses,
            missing: deriveMissingPermissions(current.missing, statuses),
          };
        });
      } finally {
        inFlight = false;
      }
    };

    void tick();
    interval = window.setInterval(() => void tick(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [modal.open, modal.mode, missingKey, setModal]);

  const handleRequestPermission = useCallback(
    async (permission: PermissionName) => {
      await requestPermission(permission);
      if (!modal.open) return;
      if (modal.mode === 'computer_use') return;
      await refreshStatuses(modal.mode, modal.missing);
    },
    [modal.mode, modal.missing, modal.open, refreshStatuses]
  );

  const presentation = resolvePresentationVariant(variant, modal.mode);

  if (presentation === 'dialog') {
    return (
      <DesktopPermissionModal
        open={modal.open}
        mode={modal.mode}
        missing={modal.missing}
        statuses={modal.statuses}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
        onRequestPermission={(permission) => void handleRequestPermission(permission)}
      />
    );
  }

  if (presentation === 'overlay-card' && modal.open) {
    return (
      <div className="pointer-events-auto absolute inset-0 z-50">
        <DesktopPermissionOverlayCard
          mode={modal.mode}
          missing={modal.missing}
          statuses={modal.statuses}
          onDismiss={closeModal}
          onRequestPermission={(permission) => void handleRequestPermission(permission)}
        />
      </div>
    );
  }

  return null;
}
